<div class="flex justify-between px-6 py-5 bg-white">
    <p class="text-[1.125rem] text-[#0A0A0A] font-bold">Create a new group</p>
    <img
        src="/assets/images/shared/cross-black-icon.svg"
        alt="Close"
        class="h-6 w-6 cursor-pointer"
        (click)="onCancel()" />
</div>

<form [formGroup]="groupForm" (ngSubmit)="submit()">
    <mat-dialog-content class="space-y-5 pb-5 border border-[#E2E8F0] bg-white">
        <!-- Group Name -->
        <div>
            <label class="block text-[#0A0A0A] text-sm font-medium mb-2" for="name">Group Name</label>
            <input
                id="name"
                type="text"
                formControlName="name"
                class="w-full form-field-container font-normal p-3 focus:outline-none"
                placeholder="What should we call this group?" />
            <!-- Error message -->
            @if (groupForm.get('name')?.invalid && groupForm.get('name')?.touched) {
                <div class="text-red-500 text-xs mt-1">Please enter a group name.</div>
            }
        </div>
        <!-- Description -->
        <div>
            <label class="block text-[#0A0A0A] text-sm font-medium mb-2" for="description">Description</label>
            <textarea
                formControlName="description"
                placeholder="Share the heart, rhythm, or vision for this space...."
                rows="4"
                class="w-full form-field-container font-normal p-3 focus:outline-none resize-none"></textarea>
            <p class="text-xs text-[#64748B]">This will appear on your group detail page.</p>
            <!-- Error message -->
            @if (groupForm.get('description')?.invalid && groupForm.get('description')?.touched) {
                <div class="text-red-500 text-xs mt-1">Please enter a group description.</div>
            }
        </div>
        <!-- Group Privacy -->
        <div>
            <label class="block text-[#0A0A0A] text-sm font-medium mb-2" for="privacy">Group Privacy</label>
            <div class="flex justify-between gap-3">
                <!-- Public -->
                <button
                    type="button"
                    class="privacy-button"
                    [ngClass]="
                        groupForm.controls.privacy.value === PrivacyTypeEnum.Public
                            ? 'active-button'
                            : 'inactive-button'
                    "
                    (click)="selectPrivacyType(PrivacyTypeEnum.Public)">
                    <div class="flex justify-between items-center mb-1">
                        <p class="text-[#0F172A] font-semibold text-sm">Public</p>
                        <div
                            [ngClass]="
                                groupForm.controls.privacy.value === PrivacyTypeEnum.Public
                                    ? 'w-2 h-2 rounded-full bg-[#4F66FF]'
                                    : 'hidden'
                            "></div>
                    </div>
                    <p class="text-xs text-[#4A5568]">Visible to everyone on Interseed.</p>
                </button>
                <!-- Private -->
                <button
                    type="button"
                    class="privacy-button"
                    [ngClass]="
                        groupForm.controls.privacy.value === PrivacyTypeEnum.Private
                            ? 'active-button'
                            : 'inactive-button'
                    "
                    (click)="selectPrivacyType(PrivacyTypeEnum.Private)">
                    <div class="flex justify-between items-center mb-1">
                        <p class="text-[#0F172A] font-semibold text-sm">Private</p>
                        <div
                            [ngClass]="
                                groupForm.controls.privacy.value === PrivacyTypeEnum.Private
                                    ? 'w-2 h-2 rounded-full bg-[#4F66FF]'
                                    : 'hidden'
                            "></div>
                    </div>
                    <p class="text-xs text-[#4A5568]">Only approved members can join.</p>
                </button>
            </div>
        </div>
        <!-- Categories -->
        <div>
            <label class="block text-[#0A0A0A] text-sm font-medium mb-1" for="cats">Categories</label>
            <p class="text-xs text-[#64748B] mb-2">
                Pick themes that describe your group. Add your own if you do not see it listed.
            </p>
            <div class="flex flex-wrap gap-2 mb-3">
                @for (category of selectedGroupCategories(); track category.id) {
                    <p class="px-3 py-1 rounded-full border text-[0.8rem] font-medium border-[#E2E8F0] bg-white">
                        {{ category.name }}
                    </p>
                }
            </div>
            <div>
                <div class="flex flex-wrap gap-2">
                    @for (category of groupCategories().slice(0, visibleCategoryCount); track category.id) {
                        <button
                            type="button"
                            class="px-3 py-1 rounded-full border text-[0.8rem] font-medium transition duration-150"
                            [ngClass]="isCategorySelected(category) ? 'active-tag' : 'inactive-tag'"
                            (click)="toggleCategory(category)">
                            {{ category.name }}
                        </button>
                    }
                    @if (groupCategories().length > visibleCategoryCount) {
                        <p class="flex items-center text-[#4F66FF] cursor-pointer" (click)="openCategoryPopup()">
                            Show all categories >
                        </p>
                    }
                </div>
            </div>
        </div>
        <!-- Group Cover Image -->
        <div class="pb-5">
            <label class="block text-[#0A0A0A] text-sm font-medium mb-2" for="photo">Group Cover</label>
            @if (!groupImageUrl) {
                <img
                    alt="UploadCover"
                    src="/assets/images/shared/upload-cover-placeholder.svg"
                    class="cursor-pointer"
                    (click)="fileInput.click()" />
                <!-- Error message -->
                @if (groupForm.get('photo')?.invalid && groupForm.get('photo')?.touched) {
                    <div class="text-red-500 text-xs mt-1">Please upload a group cover image.</div>
                }
                <!-- Upload Image -->
                <input #fileInput type="file" accept="image/*" class="hidden" (change)="onFileSelected($event)" />
            } @else if (groupImageUrl) {
                <div class="relative inline-block">
                    <img alt="Preview" class="rounded-lg block max-w-full max-h-[600px] h-auto" [src]="groupImageUrl" />
                    <button
                        type="button"
                        class="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black rounded-full"
                        (click)="removeGroupImage()">
                        <img src="/assets/images/shared/x-white-icon.svg" alt="Close" class="h-4 w-auto" />
                    </button>
                </div>
            }
        </div>
    </mat-dialog-content>

    <mat-dialog-actions>
        <button type="submit" class="border-button rounded-full px-8 py-2.5">Create Group</button>
    </mat-dialog-actions>
</form>
